/* eslint-disable max-len  */

import { useI18n } from 'vue-i18n';
import {
    eq,
    lt,
    plus,
    toFormat,
} from '@/utils/number';
import { useStore } from 'vuex';
import {
    computed,
} from 'vue';
import dayjs from 'dayjs';
import { formatUTCTime } from '@/utils/time';
import { jumpAppPage, openBrowser } from '@/utils/appBridge';

/**
 * 生成按钮文案和类型
 *
 * @param {string} btn 按钮类型
 * @param {string} currency 当前池子币种
 * @param {Object} t 多语言
 * @return {Array} [{ text: 显示文案, type: 按钮类型 }]
 */
const getBtnType = (btn, currency, t) => {
    // b池特有
    // 'buy': Buy BTC
    // 'commitMore': Commit More
    // u池特有
    // 'moreTicket': Get More Tickets
    // 'depositTrade': Deposit/Trade
    // 'deposit': Deposit USDT
    // 'tradeNow': Trade Now
    // 'vip': 升级 VIP 和 交易
    // 公有
    // 'register': Register Now
    // 'commitNow': Commit Now
    // 'wait': Sale Starts in {countdown} 等待认购
    // 'login': Login to Check 登录
    const btnMap = {
        buy: [{ text: t('lpn_buy', { currency }), type: 'buy' }],
        commitMore: [{ text: t('lpn_commit_more'), type: 'commitMore' }],
        ticketMore: [{ text: t('lpn_get_more_ticket'), type: 'moreTicket' }],
        ticketDeposit: [
            { text: t('lpn_get_more_ticket'), type: 'moreTicket' },
            { text: t('lpn_deposit', { currency }), type: 'deposit' },
        ],
        ticketDepositTrade: [
            { text: t('lpn_get_more_ticket'), type: 'moreTicket' },
            { text: t('lpn_deposit_trade'), type: 'depositTrade' },
        ],
        ticketTradeNow: [
            { text: t('lpn_get_more_ticket'), type: 'moreTicket' },
            { text: t('lpn_go_trade'), type: 'tradeNow' },
        ],
        deposit: [{ text: t('lpn_deposit', { currency }), type: 'deposit' }],
        depositTrade: [{ text: t('lpn_deposit_trade'), type: 'depositTrade' }],
        tradeNow: [{ text: t('lpn_go_trade'), type: 'tradeNow' }],
        vipTrade: [{ text: t('lpn_vip_trade_btn'), type: 'tradeNow' }],
        vip: [
            { text: t('lpn_upgrade_vip_btn_text'), type: 'upgrade' },
            { text: t('lpn_vip_trade_btn'), type: 'tradeNow' },
        ],
        notVip: [
            { text: t('lpn_upgrade_to_vip'), type: 'upgrade' },
            { text: t('lpn_vip_trade_btn'), type: 'tradeNow' },
        ],
        commitNow: [{ text: t('lpn_btn_join_now'), type: 'commitNow' }],
        register: [{ text: t('registerNow'), type: 'register' }],
        wait: [{ text: t('saleStartsIn'), type: 'wait' }],
        waitTicket: [
            { text: t('saleStartsIn'), type: 'wait' },
            { text: t('lpn_get_more_ticket'), type: 'moreTicket' },
        ],
        login: [{ text: t('loginToCheck'), type: 'login' }],
    };
    return btnMap[btn];
};

// 获得快照状态
export function getStatus(amount, minAmount) {
    if (eq(minAmount, 0)) {
        return 'success';
    }
    if (amount === '--') {
        return 'empty';
    }
    if (lt(amount, minAmount)) {
        return 'faile';
    }
    return 'success';
}

export function useLaunchpad() {
    const { t } = useI18n();
    const store = useStore();
    // 是否登录
    const isLogin = computed(() => store.state.user.userstate !== -1);

    const isM = window.sfe.isMobile || window.sfe.isApp;

    /**
     * 以秒单位格式话时间戳
     *
     * @param {number|string} ts 13位时间戳
     * @return {number} 1635053035000
     */
    const formatSecond = (ts) => parseInt(`${String(ts).slice(0, 10)}000`, 10);

    const computeStatus = (data, nowTs) => {
        let status = 1; // 默认是资产统计开始前
        const projectStartTime = formatSecond(data.projectStartTime); // 项目开盘时间
        const allocationEndTime = formatSecond(data.allocationEndTime); // 代币分配结束时间
        const allocationStartTime = formatSecond(data.allocationStartTime); // 代币分配开始时间
        const endTime = formatSecond(data.endTime); // 投入结束时间
        const startTime = formatSecond(data.startTime); // 投入开始时间
        const snapshotEndTime = formatSecond(data.snapshotEndTime); // 资产统计结束时间
        const snapshotStartTime = formatSecond(data.snapshotStartTime); // 资产统计开始时间

        if (nowTs >= projectStartTime) { // 项目已开盘以后（包含）
            status = 8;
        } else if (nowTs > allocationEndTime) { // 代币分配结束时间（不包含） 至 项目开盘时间（不包含）
            status = 7;
        } else if (nowTs >= allocationStartTime) { // 代币分配开始时间（包含） 至 代币分配结束时间（包含）
            status = 6;
        } else if (nowTs > endTime) { // 投入结束时间（不包含） 至 代币分配开始时间（不包含）
            status = 5;
        } else if (nowTs >= startTime) { // 投入开始时间（包含） 至投入结束时间（包含）
            status = 4;
        } else if (nowTs > snapshotEndTime) { // 资产统计结束时间（不包含）至 投入开始时间（不包含）
            status = 3;
        } else if (nowTs >= snapshotStartTime) { // 资产统计开始时间（包含）至 资产统计结束时间（包含）
            status = 2;
        }

        return status;
    };

    /**
     * 获取文案和按钮信息
     *
     * @param {Object} params 参数
     * @param {Object} params.detail 项目详情，包含bit池子对象和u池子对象
     * @param {Object} params.qualifications 用户项目资格,包含 subscribeQual 和 lotteryQual
     * @param {Object} params.result 认购结果
     * @param {number} params.type 0: bit池，1: usdt池
     * @param {number} params.balance mnt: mnt余额, usdt: usdt余额
     * @return {Object} 包含文案和按钮
     */
    const setTitle = ({
        detail = {},
        qualifications = {},
        result = {},
        type = 0,
        balance = {},
    }) => {
        const state = {
            // [{ text: '', type: '' }...]
            // 无 type 或 type: 'default' 普通文案，'calculator' 携带查看计算器按钮, 'ticket' 签数提示文案
            title: [],
            // [{ text: '', type : '' }]
            btn: [],
        };

        if (!detail.id) return state;

        const isSubscribe = !type; // 是否为认购池
        const sonDetail = isSubscribe // 当前池子的详细信息
            ? detail.subscribeDetail || {}
            : detail.lotteryDetail || {};
        const isVIPProject = detail?.projectType === 1; // 是否是 VIP 项目
        const isVIPUser = !!detail?.isVip; // 是否是 VIP 用户
        const isHighVIP = sonDetail?.vipList?.slice?.(0, 5)?.at?.(-1)?.level === Math.min(detail?.vipLevel, 5);
        const day = 24 * 60 * 60 * 1000;
        const snapshotEndTimePrevDay = (Math.floor(detail?.snapshotEndTime / day) - 1) * day + day - 1000;
        const firstText = t('lpn_vip_lottery_title');

        const { subscribeQual = {}, lotteryQual = {} } = qualifications;
        const { qualify } = isSubscribe ? subscribeQual : lotteryQual;

        const {
            status, // 状态
            currencyName: token = '--', // 项目名
            signup,
        } = detail;

        const {
            avgPosition = '--', // 平均持仓
            minPledgeAmount = 0, // 持仓门槛
            avgTradingAmount = '--', // 平均交易量
            minTradingAmount = 0, // 交易量门槛
            currencyName: currency = '--', // 池子名称，例如BIT，USDT
            ticketNumber: ticketNumberString = '0', // 当前获得签数
            maxTicketNumber: maxTicketNumberString = '0', // 最高可获得签数
            limitAmount = '--', // 投入币种硬顶数量
        } = sonDetail;

        // 用户账户资产
        const userBalance = isSubscribe ? (balance?.mnt || 0) : (balance?.usdt || 0);
        // 是否快照开始+27小时前
        const isBeforeSnapshotStartPlus27h = dayjs().isBefore(dayjs(detail?.snapshotStartTime).add(27, 'h'));

        const ticketNumber = Number(ticketNumberString);
        const maxTicketNumber = Number(maxTicketNumberString);

        // TODO 对于判断添加认购池和抽奖池的判断
        const isNoTrading = minTradingAmount === 0; // 交易门槛是否为0
        const isMaxTicket = ticketNumber === maxTicketNumber && ticketNumber !== 0; // 是否达到最大签数

        const snapResultTime = formatUTCTime(plus(detail.snapshotStartTime, 86400000), 'YYYY-MM-DD'); // 持仓快照下一天
        const startTimeFormat = formatUTCTime(detail.startTime, 'YYYY-MM-DD HH:mm'); // 认购开始时间格式化
        const projectSTFormat = formatUTCTime(detail.projectStartTime, 'YYYY-MM-DD HH:mm'); // 项目上线时间格式化

        // 持仓快照状态 empty: 未统计，faile: 持仓小于门槛，success: 持仓大于等于门槛
        const pledgeStatus = getStatus((isBeforeSnapshotStartPlus27h ? userBalance : avgPosition), minPledgeAmount);
        // 交易量快照状态 empty: 未统计，faile: 持仓小于门槛，success: 持仓大于等于门槛
        const tradingStatus = isVIPProject && !isSubscribe
            ? getStatus(sonDetail.currentTradingAmount, sonDetail.minTradingAmount)
            : getStatus(avgTradingAmount, minTradingAmount);
        // 是否报名
        const isSignup = signup === 1;
        const isSnapStartBeforePlus27h = dayjs().isBefore(dayjs(detail?.snapshotStartTime).add(27, 'h'));
        let canJoin;

        if (isSubscribe) {
            // 是 bit 池
            canJoin = pledgeStatus === 'success';
        } else if (isVIPProject) {
            // VIP 项目的 U 池. 满足交易量 && [30 天交易量不足, 账户不是机构用户/子账号]
            canJoin = tradingStatus === 'success' && ![5, 6, 7].includes(qualify) && isVIPUser;
        } else {
            // 非 VIP 项目的 U 池
            canJoin = tradingStatus === 'success' && pledgeStatus === 'success' && ![7].includes(qualify);
        }

        // 认购池和抽奖池区分对立的文案
        const useText = computed(() => {
            const textMap = {
                // 认购
                0: {
                    kycText: t('lpn_kyc_subscribe'),
                    snapshotResultText: t('lpn_holdings_calculated', { time: startTimeFormat }),
                },
                // 投票
                1: {
                    kycText: t('lpn_kyc_lottery'),
                    snapshotResultText: t('lpn_holdings_trading_calculated', { time: startTimeFormat }),
                },
            };

            return textMap[type];
        });

        let title = '';
        let btn = '';

        if (status === 1 && (isSubscribe || isVIPProject)) { // 预热期 & 认购|vip
            if (isSubscribe) {
                title = t('lpn_snapshot_buy_currency', { currency });
                btn = 'buy';
            } else if (isVIPProject) {
                // VIP U 池逻辑
                if (isVIPUser) {
                    title = isHighVIP
                        ? `${firstText}<br>${t('lpn_is_high_vip_tip_text', {
                            amount: sonDetail.vipMaxPledgeAmount,
                            currency,
                        })}`
                        : `${firstText}<br>${t('lpn_not_high_vip_tip_text', {
                            amount: sonDetail.vipMaxPledgeAmount,
                            currency,
                            time: formatUTCTime(snapshotEndTimePrevDay, 'YYYY-MM-DD HH:mm'),
                        })}`;
                    btn = isHighVIP
                        ? 'vipTrade'
                        : 'vip';
                } else {
                    title = `${firstText}<br>${t('lpn_not_vip_tip_text', {
                        time: formatUTCTime(snapshotEndTimePrevDay, 'YYYY-MM-DD HH:mm'),
                    })}`;
                    btn = 'notVip';
                }
            }
        } else if (status === 2 || (status === 1 && !isSubscribe && !isVIPProject)) { // 资产快照期 && 普通U池
            if (isSubscribe) { // 认购池
                if (!isLogin.value) { // 未登录
                    title = t('lpn_snapshot_buy_currency', { currency });
                } else if (pledgeStatus === 'empty') { // 持仓快照未出结果
                    title = `${t('lpn_snapshot_buy_currency', { currency })} ${t('lpn_holdings_update', { date: snapResultTime })}`;
                } else if (pledgeStatus === 'faile') { // 持仓不满足
                    title = [
                        { text: t('lpn_holdings_fail') },
                        { text: t('lpn_calculator_use'), type: 'calculator' },
                    ];
                } else if (pledgeStatus === 'success') { // 持仓满足
                    title = t('lpn_buy_more', { currency });
                }
                btn = 'buy';
            } else if (isVIPProject) {
                // VIP U 池逻辑
                if (!isLogin.value) {
                    // 未登录
                    title = `${firstText}<br>${t('lpn_only_vip_can_join')}${t('lpn_vip_detail_card_login_tip')}`;
                    btn = 'notVip';
                } else {
                    // 已登录
                    let oneSection = '';
                    let twoSection = '';
                    if (Number(sonDetail.currentTradingAmount) >= Number(sonDetail.minTradingAmount)) {
                        // 快照数据满足额度需求
                        if (isVIPUser) {
                            const pledgeAmount = (sonDetail?.vipList?.slice(0, 5)?.find(({ level }) => level === Math.min(detail?.vipLevel, 5)) || {}).pledgeAmount || 0;
                            oneSection = `${t('lpn_obtain_lottery_qualification', { amount: pledgeAmount, currency: sonDetail.currencyName })}${t('lpn_lottery_start_time_tip', { time1: formatUTCTime(detail.startTime, 'YYYY-MM-DD HH:mm') })}`;
                            if (!isHighVIP) {
                                // 不是最高等级
                                twoSection = t('lpn_not_high_vip_tip_text', {
                                    amount: sonDetail.vipMaxPledgeAmount,
                                    currency,
                                    time: formatUTCTime(snapshotEndTimePrevDay, 'YYYY-MM-DD HH:mm'),
                                });
                            }
                        } else {
                            // 普通用户
                            oneSection = `${t('lpn_trade_volume_cover_enough')}${t('lpn_cover_enough_upgrade_vip', { time: formatUTCTime(snapshotEndTimePrevDay, 'YYYY-MM-DD HH:mm') })}`;
                        }
                    } else if (sonDetail.currentTradingAmount !== '--') {
                        // 快照数据不满足额度需求
                        const startDay = (Math.floor(detail?.snapshotEndTime / day) - 29) * day;
                        oneSection = t('lpn_trade_volume_not_enough', {
                            date1: formatUTCTime(startDay, 'YYYY-MM-DD HH:mm'),
                            date2: formatUTCTime(detail?.snapshotEndTime, 'YYYY-MM-DD HH:mm'),
                        });
                    } else {
                        // 无快照数据
                        oneSection = `${firstText} ${t('lpn_trade_volume_update_time', { date: formatUTCTime(detail.snapshotStartTime + day, 'YYYY-MM-DD') })}`;
                    }
                    if (sonDetail.currentTradingAmount === '--' || Number(sonDetail.currentTradingAmount) < Number(sonDetail.minTradingAmount)) {
                        // 无资产快照数据 || 资产快照小于要求时
                        if (isVIPUser) {
                            twoSection = isHighVIP
                                ? t('lpn_is_high_vip_tip_text', {
                                    amount: sonDetail.vipMaxPledgeAmount,
                                    currency,
                                })
                                : t('lpn_not_high_vip_tip_text', {
                                    amount: sonDetail.vipMaxPledgeAmount,
                                    currency,
                                    time: formatUTCTime(snapshotEndTimePrevDay, 'YYYY-MM-DD HH:mm'),
                                });
                        } else {
                            const time = formatUTCTime(snapshotEndTimePrevDay, 'YYYY-MM-DD HH:mm');
                            twoSection = t('lpn_not_vip_tip_text', { time });
                        }
                    }
                    title = [
                        {
                            text: oneSection,
                            type: sonDetail.currentTradingAmount === '--' || Number(sonDetail.currentTradingAmount) >= Number(sonDetail.minTradingAmount)
                                ? ''
                                : 'calculator',
                        },
                        ...(twoSection ? [{ text: twoSection }] : []),
                    ];
                    if (isVIPUser) {
                        btn = isHighVIP
                            ? 'vipTrade'
                            : 'vip';
                    } else {
                        btn = 'notVip';
                    }
                }
            } else { // 抽奖池
                // 普通 U 池逻辑
                // eslint-disable-next-line no-lonely-if
                if (!isLogin.value || !isSignup) { // 1. 未登录|未报名
                    title = isNoTrading
                        ? t('lpn_add_holdings', { currency })
                        : t('lpn_add_holdings_trading', { currency });
                    btn = 'register';
                } else if ( // 2. 持仓快照未返回
                    pledgeStatus === 'empty' || tradingStatus === 'empty'
                ) {
                    title = isNoTrading
                        ? `${t('lpn_add_holdings', { currency })} ${t('lpn_holdings_update', { date: snapResultTime })}`
                        : `${t('lpn_add_holdings_trading', { currency })} ${t('lpn_holdings_trading_update', { date: snapResultTime })}`;
                    btn = isNoTrading ? 'deposit' : 'depositTrade';
                } else if ( // 3. 持仓交易量都未满足
                    pledgeStatus === 'faile' && tradingStatus === 'faile'
                ) {
                    title = [{ text: `${t('lpn_holdings_trading_fail')} ${t('lpn_calculator_use')}`, type: 'calculator' }];
                    btn = 'depositTrade';
                } else if ( // 4. 持仓满足，交易量不满足
                    pledgeStatus === 'success' && tradingStatus === 'faile'
                ) {
                    title = [{ text: `${t('lpn_trading_fail')} ${t('lpn_calculator_use')}`, type: 'calculator' }];
                    btn = 'tradeNow';
                } else if ( // 5. 持仓不满足，交易量满足, 未达到最大签数
                    pledgeStatus === 'faile' && tradingStatus === 'success'
                    && !isMaxTicket
                ) {
                    title = [{ text: `${t('lpn_holdings_fail')} ${t('lpn_calculator_use')}`, type: 'calculator' }];
                    btn = 'deposit';
                } else if ( // 6. 持仓满足，交易量满足，未到最大签数
                    pledgeStatus === 'success' && tradingStatus === 'success' && !isMaxTicket
                ) {
                    title = [
                        ...ticketNumber > 0
                            ? [{
                                text: t('lpGetTicketTip', {
                                    tickets: ticketNumber,
                                }),
                                type: 'ticket',
                            }]
                            : [],
                        { text: t('lpGetTicketCongratulation', { time: startTimeFormat }) },
                    ];
                    btn = 'waitTicket';
                } else if ( // 7. 持仓不满足，交易量满足，达到最大签数
                    pledgeStatus === 'faile' && tradingStatus === 'success' && isMaxTicket
                ) {
                    title = [
                        ...ticketNumber > 0 ? [{ text: t('lpn_get_ticket', { tickets: ticketNumber }), type: 'ticket' }] : [],
                        { text: `${t('lpn_holdings_fail')} ${t('lpn_calculator_use')}`, type: 'calculator' },
                    ];
                    btn = 'deposit';
                } else if ( // 8. 持仓满足，交易量满足，达到最大签数
                    pledgeStatus === 'success' && tradingStatus === 'success' && isMaxTicket
                ) {
                    title = [
                        // ...ticketNumber > 0 ? [{ text: t('lpn_get_ticket', { tickets: ticketNumber }), type: 'ticket' }] : [],
                        // { text: t('lpn_lottery_enbale', { time: startTimeFormat }) },
                        ...ticketNumber > 0 ? [{
                            text: t('lpGetTicketTip', {
                                tickets: ticketNumber,
                            }),
                            type: 'ticket',
                        }] : [],
                        { text: t('lpGetTicketCongratulation', { time: startTimeFormat }) },
                    ];
                    btn = 'wait';
                }
            }
        } else if (status === 3) { // 资产统计阶段
            if (!isLogin.value || !isSignup) { // 未登录 | 未报名
                title = t('lpn_log_snapshot');
                btn = 'register';
            } else if (isVIPProject && !isSubscribe) { // U池VIP
                if (isVIPUser) {
                    // VIP 用户
                    if (qualify === 2) { // 已登录未 KYC
                        title = [
                            { text: `${t('lpn_vip_not_kyc_tip1')} ${t('lpn_vip_not_kyc_tip2', { time: formatUTCTime(detail?.startTime, 'YYYY-MM-DD HH:mm') })}` },
                            { text: t('lpn_kyc_lottery') },
                        ];
                    } else { // 已登录已 KYC
                        title = `${t('lpn_vip_not_kyc_tip1')} ${t('lpn_vip_not_kyc_tip2', { time: formatUTCTime(detail?.startTime, 'YYYY-MM-DD HH:mm') })}`;
                    }
                } else {
                    // 非 VIP 用户
                    title = t('lpn_vip_trade_val_fail');
                }
            } else {
                // 普通U池 & 已登录&已报名
                btn = 'wait';
            }
        } else if (status === 4) { // 认购阶段
            if (!isLogin.value) { // 1. 未登录
                title = t('lpn_log_snapshot');
                btn = 'commitNow';
            } else if (subscribeQual.qualify === 4) { // 5 6 已认购bit池（优先级高放上边）
                if (!isSubscribe) { // u池文案
                    title = t('lpn_join_step4_date', { date: projectSTFormat });
                } else if (lt(detail.subscribeDetail.pledgeAmount, detail.subscribeDetail.avgPosition)) { // 5. 未达认购上限
                    title = t('lpn_higher_commit', { token, limitAmount: toFormat(limitAmount) });
                    btn = !isM ? 'commitMore' : '';
                } else { // 6. 已达认购上限
                    title = [
                        { text: t('lpn_allocation_announced_time', { time: projectSTFormat }) },
                        { text: t('lpn_join_step4_date', { date: projectSTFormat }) },
                    ];
                }
            } else if (lotteryQual.qualify === 4) { // 7. 已认购usdt池（优先级高放上边）
                title = isSubscribe
                    ? t('lpn_join_step4_date', { date: projectSTFormat })
                    : [
                        { text: t('lpn_lottery_announced_time', { time: projectSTFormat }) },
                        { text: t('lpn_join_step4_date', { date: projectSTFormat }) },
                    ];
            } else if (!canJoin) { // 2. 未满足认购条件
                // VIP 项目 和普通项目 U 池
                // 账户为子账户、机构账户等 ? 账户不允许信息 : 交易量不满足信息
                title = qualify === 7
                    ? t('lpn_vip_not_join_account_tip')
                    : t('lpCantToSubscribeTrade');
            } else if (qualify === 2) { // 3. 未过KYC
                title = isSubscribe
                    ? `${t('lpn_enable_subscribe')} ${t('lpn_kyc_subscribe')}`
                    : [
                        ...ticketNumber > 0 ? [{ text: t('lpGetTicketTip', { tickets: ticketNumber }), type: 'ticket' }] : [],
                        { text: `${t('lpn_enable_lottery')} ${t('lpn_kyc_lottery')}` },
                    ];
                btn = 'commitNow';
            } else if (subscribeQual.qualify !== 4 && lotteryQual.qualify !== 4 && qualify === 0) { // 4. 满足条件未认购
                title = isSubscribe
                    ? t('lpn_enable_subscribe')
                    : [
                        ...ticketNumber > 0 ? [{ text: t('lpGetTicketTip', { tickets: ticketNumber }), type: 'ticket' }] : [],
                        { text: t('lpn_enable_lottery') },
                    ];
                btn = 'commitNow';
            }
        } else if ([5, 6, 7].includes(status)) { // 公布前
            if (!isLogin.value) {
                btn = 'login';
            }
        } else if (result.isCalculated !== 1) { // 代币分配
            title = isSubscribe
                ? [{ text: t('lpn_allocation_announced_time', { time: projectSTFormat }) }]
                : [{ text: t('lpn_lottery_announced_time', { time: projectSTFormat }) }];
            title.push({ text: t('lpn_join_step4_date', { date: projectSTFormat }) });
        }

        if (typeof title === 'string' && title !== '') {
            state.title = [{ text: title }];
        } else {
            state.title = title;
        }

        state.btn = getBtnType(btn, currency, t);

        return state;
    };

    const setNotice = (
        detail = {}, qualifications = {}, result = {},
    ) => {
        const { subscribeDetail = {}, lotteryDetail = {} } = detail; // 认购详情，投票详情
        const { subscribeQual = {}, lotteryQual = {} } = qualifications; // 认购资格，投票资格
        const { status, tradingPairs } = detail;
        // 是否已分配完成出结果并且参加了活动
        const isJoin = detail.status >= 5 && result.isCalculated === 1 && result.isJoin === 1;
        const subName = subscribeDetail.currencyName || '--';
        const lotName = lotteryDetail.currencyName || '--';
        let options = [];

        let title = '';
        let btnType = 0;// 0:不显示按钮 1: 立即登录, 2: 立即完成KYC认证, 3: 立即充值, 4: 立即认购，5: 立即追加, 6:立即投注
        // 结果公布
        if (result.isCalculated === 1) {
            // 登录已参加
            if (isLogin.value && isJoin) {
                title = (status > 7 && tradingPairs?.length > 1) ? `${t('lpn_notice11')} ${t('lpn_notice17')}` : t('lpn_notice11'); // 本期活动已完成，请查看您的收益
            } else {
                // 未登录或登录未参加
                title = (status > 7 && tradingPairs?.length > 1) ? `${t('lpn_notice10')} ${t('lpn_notice17')}` : t('lpn_notice10'); // 本期活动已完成
            }
            if (status > 7) {
                options = tradingPairs || ['USDT'];
                btnType = 7;
            } else {
                btnType = 0;
            }
        } else if (status >= 5) {
            // 分配期
            title = t('lpn_token_calculation'); // 活动结果统计中，即将完成
            btnType = 0;
        } else if (!isLogin.value) {
            // 未登录
            title = t('lpn_card_notice1'); // 登录并完成KYC认证
            btnType = 1;
        } else if (subscribeQual.qualify === 2) {
            title = t('lpn_notice2'); // '只差一步即可参与LaunchPad活动（查看受限制国家）';
            btnType = 2;
        } else if (status === 4) { // 认购期
            if (subscribeQual.qualify === 1 && (lotteryQual.qualify === undefined || lotteryQual.qualify === 1)) {
                title = t('lpn_notice7'); // '很遗憾您的持仓不满足活动要求，请关注我们的下期活动';
                btnType = 0;
            } else if (subscribeQual.qualify === 0 && lotteryQual.qualify === 0) {
                title = t('lpn_notice15'); // 活动进行中，请选择您的参与方式
                btnType = 0;
            } else if (subscribeQual.qualify === 4 || lotteryQual.qualify === 4) {
                // 您可以追加BIT投入获得更多分配额度/您的抽奖号为**，请关注开奖结果
                title = subscribeQual.qualify === 4
                    ? t('lpn_notice9', { currency: subName }) // `您可以追加${subName}投入获得更多分配额度`
                    : t('lpn_notice16', { number: lotteryDetail.luckNumber }); // `您的抽奖号为${lotteryDetail.luckNumber}，请关注开奖结果`;
                btnType = subscribeQual.qualify === 4 ? 5 : 0;
            } else if (!(subscribeQual.qualify && lotteryQual.qualify)) { // 认购/活动进行中，立即通过BIT认购/USDT投注
                title = subscribeQual.qualify === 0
                    ? t('lpn_notice8', { currency: subName }) // `认购进行中，立即通过${subName}认购`
                    : t('lpn_notice14', { currency: lotName }); // `投注进行中，立即通过${lotName}投注`;
                btnType = subscribeQual.qualify === 0 ? 4 : 6;
            }
        } else if (status === 3) { // 统计期
            title = lotteryDetail.currencyName ? t('lpn_card_notice8') : t('lpn_notice_13'); // '快照期已结束，即将开放认购和投注';
            btnType = 0;
        } else if (status === 2) { // 快照期
            btnType = 3;
            if (subscribeQual.qualify === 1) {
                title = t('lpn_notice12', { currency: subName }); // '资产统计中，当日新增持仓第二日可查看<br>持有更多BIT,解锁BIT认购特权';
            } else if (subscribeQual.qualify === 0) {
                title = t('lpn_notice6', { currency: subName }); // '资产统计中，当日新增持仓第二日可查看<br>持有更多BIT，增加可认购额度';
            }
        } else if (status === 1) { // 预热期
            title = t('lpn_notice3'); // '快照即将开始，建议提前准备好持仓';
        }

        return {
            btnType,
            options,
            title,
        };
    };

    const setLoginTips = ({
        detail = {},
        qualifications = {},
        result = {},
    }) => {
        const btnMap = {
            login: [{ text: t('Login to Check'), type: 'login' }],
            verify: [{ text: t('lp_btn_kyc'), type: 'verify' }],
            commitMore: [{ text: t('lpn_commit_more'), type: 'commitMore' }],
            tradeNow: [{ text: t('lpn_go_trade'), type: 'tradeNow' }],
        };

        const state = {
            title: [],
            btn: [],
            options: [],
        };

        const {
            status, // 状态
            subscribeDetail = {},
            lotteryDetail = {},
        } = detail;

        const qualify = qualifications.subscribeQual?.qualify;

        const sPledgeStatus = getStatus(subscribeDetail.avgPosition, subscribeDetail.minPledgeAmount);
        const lPledgeStatus = getStatus(lotteryDetail.avgPosition, lotteryDetail.minPledgeAmount);
        const lTradingsStatus = getStatus(lotteryDetail.avgTradingAmount, lotteryDetail.minTradingAmount);
        const lotteryPass = lPledgeStatus === 'success' && lTradingsStatus === 'success';

        // 至少有一个池子具有认购条件
        const hasOnePass = sPledgeStatus === 'success' || lotteryPass;

        // let title = '';
        let btn = '';
        // if (status === 1) {
        //     if (!isLogin.value) {
        //         title = t('lp_no_kyc');
        //         btn = 'login';
        //     } else if (qualify === 2) {
        //         title = t('lp_no_kyc');
        //         btn = 'verify';
        //     }
        // } else if (status === 2 || status === 3) {
        //     if (!isLogin.value) {
        //         title = t('lpn_log_snapshot');
        //         btn = 'login';
        //     } else if (qualify === 2) {
        //         title = t('lp_no_kyc');
        //         btn = 'verify';
        //     }
        // } else if (status === 4) {
        //     if (!isLogin.value) {
        //         title = t('lpn_log_snapshot');
        //         btn = 'login';
        //     } else if (qualify === 2 && hasOnePass) {
        //         title = t('lp_no_kyc');
        //         btn = 'verify';
        //     } else if (qualify === 4) {
        //         btn = (isM && lt(subscribeDetail.pledgeAmount, subscribeDetail.avgPosition)) ? 'commitMore' : '';
        //     }
        // } else if (status < 8 && !isLogin.value && result.isCalculated !== 1) {
        //     title = t('lpn_log_snapshot');
        //     btn = 'login';
        // } else if (status > 7 && result.isCalculated === 1) {
        //     title = isM && detail?.tradingPairs?.length > 1 ? t('lpn_notice17') : '';
        //     btn = isM ? 'tradeNow' : '';
        //     state.options = detail?.tradingPairs || ['USDT'];
        // }

        if ([7, 8].includes(status) && !isLogin.value) {
            btn = 'login';
        }

        // if (title) state.title = [{ text: title }];
        if (btn) state.btn = btnMap[btn];

        return state;
    };

    /**
     *  轮播图条跳转方法
     *
     * @param {*} item banner item
     */
    const jumpLink = (item) => {
        const { isApp } = window.sfe;
        const { appInsideLink, appOutsideLink, webLink } = item;
        if (isApp) {
            if (appInsideLink) {
                jumpAppPage({ path: appInsideLink });
            } else if (appOutsideLink) {
                openBrowser(appOutsideLink);
            }
            return;
        }
        if (isM && appOutsideLink) {
            window.location.href = appOutsideLink;
            return;
        }
        if (!isM && webLink) window.open(webLink, '_blank');
    };

    /**
     * 签号高亮处理
     *
     * @param {Object} luckNumbers result接口的luckNumbers属性，用户所有签数
     * @param {string} lotteryRandomNumber result接口的lotteryRandomNumber属性，中奖的尾数
     * @return {Array} [{ text: '展示字段', isWin: '该签号是否中奖‘ }]
     */
    const handleTickets = (luckNumbers, lotteryRandomNumber) => {
        if (!luckNumbers || !lotteryRandomNumber) return false;
        const { winNumbers, lostNumbers } = luckNumbers;
        const winList = [];
        const loseList = lostNumbers.map((item) => ({
            text: item,
            isWin: false,
        }));
        let randomList = [];
        lotteryRandomNumber.split(',').forEach((item) => {
            randomList = randomList.concat(item.split(';'));
        });
        winNumbers.forEach((item) => {
            randomList.some((i) => {
                const reg = new RegExp(`${i}$`);
                if (reg.test(item)) {
                    winList.push({
                        text: item?.replace(reg, `<b>${i}</b>`),
                        isWin: true,
                    });
                    return true;
                }
                return false;
            });
        });
        return [...winList, ...loseList];
    };

    const handleAmount = (list = [], startTime, endTime) => {
        const dateList = [...list];
        let historyFirstTime = 0;
        if (list.length) {
            const historyFirstDate = list[0].date;
            historyFirstTime = dayjs.utc(historyFirstDate).valueOf();
        }
        const timeList = [];
        for (let time = startTime; time < endTime; time += 86400000) {
            timeList.push(formatUTCTime(time, 'YYYY-MM-DD'));
        }
        timeList.forEach((item) => {
            if (dayjs.utc(item) > historyFirstTime) {
                dateList.unshift({
                    date: item,
                    amount: '',
                    currencyName: '',
                    text: t('lpn_snapshot_update_time', { date: formatUTCTime(dayjs.utc(item).add(1, 'day'), 'YYYY-MM-DD') }),
                });
            }
        });
        return dateList;
    };

    return {
        computeStatus,
        setTitle,
        setNotice,
        jumpLink,
        handleTickets,
        handleAmount,
        setLoginTips,
    };
}
